import { SECURITY_EVENT_TYPES, DETECTION_THRESHOLDS } from '../constants';

/**
 * API Abuse Detector
 * 
 * Monitors API usage patterns to detect abuse, rate limiting violations,
 * suspicious payloads, and unauthorized access attempts.
 */
export class ApiAbuseDetector {
    constructor(onSecurityEvent) {
        this.onSecurityEvent = onSecurityEvent;
        this.initialized = false;
        this.apiCallHistory = [];
        this.rateLimitCounters = new Map();
        this.suspiciousPatterns = [];
        this.endpointBaselines = new Map();
        this.lastCleanup = Date.now();
    }

    /**
     * Initialize the API abuse detector
     */
    async initialize() {
        try {
            // Initialize suspicious payload patterns
            this.initializeSuspiciousPatterns();
            
            // Set up rate limiting counters
            this.setupRateLimitCounters();
            
            // Initialize endpoint baselines
            this.initializeEndpointBaselines();
            
            this.initialized = true;
        } catch (error) {
            console.error('RASP - Failed to initialize API Abuse Detector:', error);
            throw error;
        }
    }

    /**
     * Initialize suspicious payload patterns (MINIMAL - only obvious attacks)
     */
    initializeSuspiciousPatterns() {
        this.suspiciousPatterns = [
            // Only the most obvious SQL injection patterns
            /(\bUNION\s+SELECT\b)/gi,
            /(\bOR\s+1\s*=\s*1\b)/gi,

            // Only obvious XSS patterns
            /<script[^>]*>.*<\/script>/gi,

            // Only obvious command injection
            /(;\s*rm\s|-rf\s|&&\s*rm\s)/gi,

            // Only obvious path traversal
            /\.\.\/\.\.\/\.\.\//g,

            // Reduce false positives by being more specific
        ];
    }

    /**
     * Set up rate limiting counters
     */
    setupRateLimitCounters() {
        // Initialize counters for different time windows
        this.rateLimitCounters.set('minute', { count: 0, window: 60000, lastReset: Date.now() });
        this.rateLimitCounters.set('burst', { count: 0, window: 10000, lastReset: Date.now() });
        this.rateLimitCounters.set('hour', { count: 0, window: 3600000, lastReset: Date.now() });
    }

    /**
     * Initialize endpoint baselines for normal behavior
     */
    initializeEndpointBaselines() {
        // Common API endpoints and their expected patterns
        const commonEndpoints = [
            '/auth/device',
            '/devices/',
            '/bookings',
            '/api/soketi/auth',
            '/api/security/events'
        ];

        commonEndpoints.forEach(endpoint => {
            this.endpointBaselines.set(endpoint, {
                averageResponseTime: 0,
                callCount: 0,
                errorRate: 0,
                lastAccessed: null,
                suspiciousAttempts: 0
            });
        });
    }

    /**
     * Perform API abuse check
     */
    async performCheck() {
        if (!this.initialized) return;

        try {
            // Check rate limits
            await this.checkRateLimits();
            
            // Analyze recent API calls
            await this.analyzeRecentApiCalls();
            
            // Check for suspicious patterns in recent calls
            await this.checkSuspiciousPatterns();
            
            // Update endpoint baselines
            await this.updateEndpointBaselines();
            
            // Cleanup old data
            await this.cleanupOldData();
            
        } catch (error) {
            console.warn('RASP - API abuse check failed:', error);
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.API_ABUSE, {
                message: 'API abuse check failed',
                details: { error: error.message }
            });
        }
    }

    /**
     * Monitor an API call for abuse patterns
     */
    monitorApiCall(apiCallData) {
        try {
            const callInfo = {
                url: apiCallData.url || '',
                method: apiCallData.method || 'GET',
                payload: apiCallData.payload || {},
                headers: apiCallData.headers || {},
                timestamp: Date.now(),
                responseTime: apiCallData.responseTime || 0,
                statusCode: apiCallData.statusCode || 200,
                userAgent: apiCallData.userAgent || ''
            };

            // Add to history
            this.apiCallHistory.push(callInfo);

            // Keep only recent history (last 1000 calls or 1 hour)
            const oneHourAgo = Date.now() - 3600000;
            this.apiCallHistory = this.apiCallHistory
                .filter(call => call.timestamp > oneHourAgo)
                .slice(-1000);

            // Update rate limit counters
            this.updateRateLimitCounters();

            // Check for immediate threats
            this.checkImmediateThreats(callInfo);

        } catch (error) {
            console.warn('RASP - API call monitoring failed:', error);
        }
    }

    /**
     * Update rate limit counters
     */
    updateRateLimitCounters() {
        const now = Date.now();

        this.rateLimitCounters.forEach((counter, window) => {
            // Reset counter if window has passed
            if (now - counter.lastReset > counter.window) {
                counter.count = 1;
                counter.lastReset = now;
            } else {
                counter.count++;
            }
        });
    }

    /**
     * Check rate limits
     */
    async checkRateLimits() {
        const minuteCounter = this.rateLimitCounters.get('minute');
        const burstCounter = this.rateLimitCounters.get('burst');

        // Check minute rate limit
        if (minuteCounter.count > DETECTION_THRESHOLDS.API_RATE_LIMIT) {
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.RATE_LIMIT_EXCEEDED, {
                message: 'API rate limit exceeded (per minute)',
                details: {
                    count: minuteCounter.count,
                    limit: DETECTION_THRESHOLDS.API_RATE_LIMIT,
                    window: 'minute'
                }
            });
        }

        // Check burst rate limit
        if (burstCounter.count > DETECTION_THRESHOLDS.API_BURST_LIMIT) {
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.RATE_LIMIT_EXCEEDED, {
                message: 'API burst rate limit exceeded',
                details: {
                    count: burstCounter.count,
                    limit: DETECTION_THRESHOLDS.API_BURST_LIMIT,
                    window: 'burst'
                }
            });
        }
    }

    /**
     * Analyze recent API calls for patterns
     */
    async analyzeRecentApiCalls() {
        if (this.apiCallHistory.length < 5) return;

        const recentCalls = this.apiCallHistory.slice(-20);
        
        // Check for repeated failed requests (LESS AGGRESSIVE)
        const failedCalls = recentCalls.filter(call => call.statusCode >= 400);
        if (failedCalls.length > 50) { // Increased from 10 to 50
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.API_ABUSE, {
                message: 'Very high number of failed API requests',
                details: {
                    failedCount: failedCalls.length,
                    totalCount: recentCalls.length,
                    failureRate: (failedCalls.length / recentCalls.length * 100).toFixed(2) + '%'
                }
            });
        }

        // Check for unusual response times
        const responseTimes = recentCalls.map(call => call.responseTime).filter(time => time > 0);
        if (responseTimes.length > 0) {
            const avgResponseTime = responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
            const maxResponseTime = Math.max(...responseTimes);
            
            if (maxResponseTime > avgResponseTime * 5 && maxResponseTime > 5000) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.API_ABUSE, {
                    message: 'Unusually slow API response detected',
                    details: {
                        maxResponseTime,
                        avgResponseTime: avgResponseTime.toFixed(2),
                        threshold: avgResponseTime * 5
                    }
                });
            }
        }

        // Check for repeated identical requests (potential replay attack)
        const requestSignatures = new Map();
        recentCalls.forEach(call => {
            const signature = `${call.method}:${call.url}:${JSON.stringify(call.payload)}`;
            const count = requestSignatures.get(signature) || 0;
            requestSignatures.set(signature, count + 1);
        });

        requestSignatures.forEach((count, signature) => {
            if (count > 5) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.API_ABUSE, {
                    message: 'Repeated identical API requests detected',
                    details: {
                        signature: signature.substring(0, 100) + '...',
                        count,
                        type: 'potential_replay_attack'
                    }
                });
            }
        });
    }

    /**
     * Check for suspicious patterns in API calls
     */
    async checkSuspiciousPatterns() {
        const recentCalls = this.apiCallHistory.slice(-50);

        recentCalls.forEach(call => {
            // Check URL for suspicious patterns
            this.checkStringForSuspiciousPatterns(call.url, 'url', call);

            // Check payload for suspicious patterns
            if (call.payload && typeof call.payload === 'object') {
                const payloadString = JSON.stringify(call.payload);
                this.checkStringForSuspiciousPatterns(payloadString, 'payload', call);
            }

            // Check headers for suspicious patterns
            if (call.headers && typeof call.headers === 'object') {
                Object.entries(call.headers).forEach(([key, value]) => {
                    this.checkStringForSuspiciousPatterns(`${key}:${value}`, 'header', call);
                });
            }

            // Check user agent for suspicious patterns
            if (call.userAgent) {
                this.checkStringForSuspiciousPatterns(call.userAgent, 'user_agent', call);
            }
        });
    }

    /**
     * Check a string for suspicious patterns
     */
    checkStringForSuspiciousPatterns(str, type, callInfo) {
        if (!str || typeof str !== 'string') return;

        this.suspiciousPatterns.forEach((pattern, index) => {
            const matches = str.match(pattern);
            if (matches) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.SUSPICIOUS_PAYLOAD, {
                    message: `Suspicious pattern detected in ${type}`,
                    details: {
                        pattern: pattern.toString(),
                        matches: matches.slice(0, 5), // Limit matches to prevent large payloads
                        type,
                        url: callInfo.url,
                        method: callInfo.method,
                        timestamp: callInfo.timestamp
                    }
                });
            }
        });
    }

    /**
     * Check for immediate threats in API call
     */
    checkImmediateThreats(callInfo) {
        // Check for unauthorized access attempts
        if (callInfo.statusCode === 401 || callInfo.statusCode === 403) {
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.UNAUTHORIZED_ACCESS, {
                message: 'Unauthorized access attempt detected',
                details: {
                    url: callInfo.url,
                    method: callInfo.method,
                    statusCode: callInfo.statusCode,
                    timestamp: callInfo.timestamp
                }
            });
        }

        // Check for unusually large payloads
        if (callInfo.payload) {
            const payloadSize = JSON.stringify(callInfo.payload).length;
            if (payloadSize > 100000) { // 100KB
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.SUSPICIOUS_PAYLOAD, {
                    message: 'Unusually large payload detected',
                    details: {
                        size: payloadSize,
                        url: callInfo.url,
                        method: callInfo.method
                    }
                });
            }
        }

        // Check for suspicious HTTP methods
        const suspiciousMethods = ['TRACE', 'CONNECT', 'PATCH'];
        if (suspiciousMethods.includes(callInfo.method.toUpperCase())) {
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.API_ABUSE, {
                message: 'Suspicious HTTP method detected',
                details: {
                    method: callInfo.method,
                    url: callInfo.url
                }
            });
        }
    }

    /**
     * Update endpoint baselines
     */
    async updateEndpointBaselines() {
        this.apiCallHistory.forEach(call => {
            // Find matching baseline
            let matchingEndpoint = null;
            for (const [endpoint, baseline] of this.endpointBaselines) {
                if (call.url.includes(endpoint)) {
                    matchingEndpoint = endpoint;
                    break;
                }
            }

            if (matchingEndpoint) {
                const baseline = this.endpointBaselines.get(matchingEndpoint);
                baseline.callCount++;
                baseline.lastAccessed = call.timestamp;
                
                // Update average response time
                if (call.responseTime > 0) {
                    baseline.averageResponseTime = 
                        (baseline.averageResponseTime * (baseline.callCount - 1) + call.responseTime) / baseline.callCount;
                }

                // Update error rate
                if (call.statusCode >= 400) {
                    baseline.errorRate = 
                        (baseline.errorRate * (baseline.callCount - 1) + 1) / baseline.callCount;
                }
            }
        });
    }

    /**
     * Clean up old data
     */
    async cleanupOldData() {
        const now = Date.now();
        
        // Cleanup every 5 minutes
        if (now - this.lastCleanup > 300000) {
            // Remove old API call history (older than 1 hour)
            const oneHourAgo = now - 3600000;
            this.apiCallHistory = this.apiCallHistory.filter(call => call.timestamp > oneHourAgo);
            
            this.lastCleanup = now;
        }
    }

    /**
     * Report a security event
     */
    reportSecurityEvent(eventType, eventData) {
        if (this.onSecurityEvent) {
            this.onSecurityEvent(eventType, eventData);
        }
    }

    /**
     * Get API abuse statistics
     */
    getStatistics() {
        const now = Date.now();
        const oneHourAgo = now - 3600000;
        const recentCalls = this.apiCallHistory.filter(call => call.timestamp > oneHourAgo);

        return {
            totalCalls: this.apiCallHistory.length,
            recentCalls: recentCalls.length,
            rateLimitCounters: Object.fromEntries(this.rateLimitCounters),
            endpointBaselines: Object.fromEntries(this.endpointBaselines),
            averageResponseTime: recentCalls.length > 0 ? 
                recentCalls.reduce((sum, call) => sum + call.responseTime, 0) / recentCalls.length : 0
        };
    }

    /**
     * Cleanup the detector
     */
    cleanup() {
        try {
            this.apiCallHistory = [];
            this.rateLimitCounters.clear();
            this.endpointBaselines.clear();
            this.initialized = false;
            console.log('RASP - API Abuse Detector cleanup completed');
        } catch (error) {
            console.error('RASP - API Abuse Detector cleanup failed:', error);
        }
    }
}
